import './styles/Parts.scss';

import { graphql } from 'gatsby';
import React, {
    LegacyRef,
    useContext,
    useEffect,
    useRef,
    useState,
} from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import WZoom from 'vanilla-js-wheel-zoom/src/wheel-zoom';
import { useTranslation } from 'react-i18next';
import {
    faChevronLeft,
    faChevronRight,
    faMinus,
    faPlus,
    faTimes,
} from '@fortawesome/free-solid-svg-icons';

import {
    Icon,
    Loading,
    LocalizedLink,
} from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Primitives';
import { AddToCartButtonContainer } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import GatsbyImageWrapper from '../components/GatsbyImageWrapper';
import Layout from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Layout/Layout';
import LocalePrice from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/LocalePrice';
import SEO from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Seo';
import { feedbackContext } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/feedback/feedbackContext';
import { popFrontURL } from '../utils';
import { pushAddToCartEvent } from '@progymedia/gatsby-theme-boutik-ecommerce/src/utils/useGTM_UA';
import { useAddProductsToCartAction } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/cart/actions/useAddProductsToCartAction';
import { useAvailabilityInfo } from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Cart/AddToCartButton';
import { useCartContext } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/cart';
import useMobileDetect from '@progymedia/gatsby-theme-boutik-ecommerce/src/components/Hooks/useMobileDetect';
import { usePageLocalization } from '@sbi-international/boutik.gatsby.plugin.multilocale';
import { useProductPrice } from '@progymedia/gatsby-theme-boutik-ecommerce/src/context/store';

export const query = graphql`
    query Parts($code: String!) {
        productsJson(code: { eq: $code }) {
            code
            id
            title {
                en_CA
                en_US
                fr_CA
            }
            category
            slug {
                en_CA
                en_US
                fr_CA
            }
            schema {
                en_CA
                en_US
                fr_CA
            }
            fields {
                schema {
                    childImageSharp {
                        gatsbyImageData(layout: CONSTRAINED)
                    }
                }
            }
            images
            fields {
                ...productsFieldsImagesFragment
            }
            attributes {
                serial_number_from {
                    values {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
                serial_number_to {
                    values {
                        en_CA
                        en_US
                        fr_CA
                    }
                }
            }
            parts {
                code
                id
                title {
                    en_CA
                    en_US
                    fr_CA
                }
                attributes {
                    schema_number {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                    nb_per_appliance {
                        values {
                            en_CA
                            en_US
                            fr_CA
                        }
                    }
                }
            }
        }
    }
`;

export default function Parts({ data }: { data: Queries.PartsQuery }) {
    const product = data.productsJson;
    const { currentLocale } = usePageLocalization();
    const { t } = useTranslation('layout');
    const [wzoom, setWzoom]: [any, any] = useState({});
    const imgRef = useRef();

    const isMobile = useMobileDetect().isMobile();
    const [pdfScale, setPdfScale] = useState(isMobile ? 80 : 100);
    const [coords, setCoords] = useState<any[]>([]);
    const [pdfPage, setPdfPage] = useState(null);
    const [pageNumber] = useState(1);
    const [pdfAnnotations, setAnnotations] = useState([]);

    const getSchemaAnnotations = async (schema: string) => {
        const pdfDocument = await pdfjs
            .getDocument(`/file/${schema}`)
            .promise.then((doc: any) => {
                return doc;
            });
        const page = await pdfDocument.getPage(1);
        const annotations = await page.getAnnotations();
        setPdfPage(page);
        setAnnotations(annotations);
    };

    useEffect(() => {
        setWzoom(1); // Force un refresh du component
        for (let index = 0; index < pdfAnnotations.length; index++) {
            const rect = pdfAnnotations[index].rect;
            const view = pdfPage?.view;
            const normalizedRect = pdfjs.Util.normalizeRect([
                rect[0],
                view[3] - rect[1],
                rect[2],
                view[3] - rect[3],
            ]);
            const newCoord = {
                x: normalizedRect[0],
                y: normalizedRect[1],
                note: pdfAnnotations?.[index]?.unsafeUrl,
            };
            const newCoords = coords;
            newCoords.push(newCoord);
            setCoords(newCoords);
        }
    }, [coords, pdfAnnotations, pdfPage?.view]);

    useEffect(() => {
        setWzoom(
            WZoom.create('#parts-img', {
                zoomOnClick: false,
                maxScale: 10,
                minScale: 0.35,
                speed: 12,
            })
        );
        pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.js`;
    }, []);

    const explodedParts = [] as any;
    product?.parts?.forEach((part: any) => {
        const schemaNumber =
            part?.attributes?.schema_number?.values[0][currentLocale];

        if (schemaNumber?.includes(',')) {
            schemaNumber.split(',').forEach((partNumber: any) => {
                const newPart = JSON.parse(JSON.stringify(part));

                newPart.attributes.schema_number.values[0][currentLocale] =
                    partNumber;

                explodedParts.push(newPart);
            });
        } else {
            explodedParts.push(part);
        }
    });

    const comparePart = (a: any, b: any) => {
        let intervalStart;

        if (a?.includes(',')) {
            intervalStart = a?.substring(0, 2);
            return intervalStart - b;
        }

        if (b?.includes(',')) {
            intervalStart = b?.substring(0, 2);
            return a - intervalStart;
        }

        return a - b;
    };

    const serialNumberFrom =
        product?.attributes?.serial_number_from?.values?.[0]?.['en_CA'];
    const serialNumberTo =
        product?.attributes?.serial_number_to?.values?.[0]?.['en_CA'];

    const [schemaIndex, setSchemaIndex] = useState(0);

    function updateSchemaIndex(modifier: number) {
        if (
            schemaIndex + modifier >= 0 &&
            schemaIndex + modifier < (product?.schema?.length ?? 0)
        )
            setSchemaIndex(schemaIndex + modifier);
    }
    function updatePdfScale(modifier: number) {
        if (pdfScale + modifier > 0 && pdfScale + modifier < 200)
            setPdfScale(pdfScale + modifier);
    }

    const endsWithPdf =
        product?.schema?.[schemaIndex]?.[currentLocale]?.endsWith('.pdf');
    const hasMultipleSchema = (product?.schema?.length ?? 0) > 1;

    function isProductDiscontinued() {
        return product?.category?.includes('Discontinued');
    }

    const productTitle = product?.title?.[currentLocale] as string;

    const sortedSchema = () => {
        if (product?.schema != null) {
            const schema = product?.schema as Array<any>;

            return schema.sort((a, b) => {
                return a.en_CA.localeCompare(b.en_CA);
            });
        } else {
            return [];
        }
    };

    return (
        <Layout>
            <SEO
                title={t('metadata:part.title', {
                    SKU: product?.code,
                })}
                description={
                    t('metadata:part.description', {
                        TITLE: productTitle,
                        SKU: product?.code,
                    }) as string
                }
            />
            <div className='parts-banner-wrapper'>
                <div className='parts-banner'>
                    <GatsbyImageWrapper
                        fields={product?.fields as Queries.ProductsJsonFields}
                    />

                    <div className='product-info'>
                        <h1 className='title'>
                            {product?.title?.[currentLocale]}
                        </h1>
                        <span className='code'>{product?.code}</span>

                        <span>
                            {serialNumberFrom &&
                            !serialNumberFrom?.startsWith('-') &&
                            serialNumberTo &&
                            !serialNumberTo?.startsWith('-') &&
                            serialNumberTo !== 'NULL'
                                ? t('parts.serial-fromto', {
                                      from: serialNumberFrom,
                                      to: serialNumberTo,
                                  })
                                : serialNumberFrom === undefined ||
                                  serialNumberFrom?.startsWith('-')
                                ? '--'
                                : t('parts.serial-from', {
                                      from: serialNumberFrom,
                                  })}
                        </span>

                        <span className='line' />

                        {!isProductDiscontinued() && (
                            <LocalizedLink
                                className='link'
                                to={popFrontURL(
                                    product?.slug?.[currentLocale],
                                    2
                                )}
                                label={t('parts.view-product') as string}
                            />
                        )}
                    </div>
                </div>
            </div>
            <div className='container parts'>
                <div className='primary'>
                    <div className='schema-slider'>
                        {endsWithPdf && hasMultipleSchema && (
                            <span className='page'>{`${schemaIndex + 1} / ${
                                product?.schema?.length
                            }`}</span>
                        )}
                        {endsWithPdf && hasMultipleSchema && (
                            <FontAwesomeIcon
                                className='arrow left'
                                icon={faChevronLeft}
                                onClick={() => updateSchemaIndex(-1)}
                            />
                        )}
                        {endsWithPdf && (
                            <div className='pdf-scale'>
                                <FontAwesomeIcon
                                    className='scale'
                                    icon={faPlus}
                                    onClick={() => updatePdfScale(20)}
                                />
                                <FontAwesomeIcon
                                    className='scale'
                                    icon={faMinus}
                                    onClick={() => updatePdfScale(-20)}
                                />
                            </div>
                        )}
                        {product?.schema && (
                            <div
                                className={
                                    endsWithPdf
                                        ? 'part-schema pdf'
                                        : 'part-schema'
                                }
                            >
                                {endsWithPdf ? (
                                    <Document
                                        className='pdf-doc'
                                        file={`/file/${
                                            sortedSchema()[schemaIndex]?.[
                                                currentLocale
                                            ]
                                        }`}
                                        loading={<Loading />}
                                        onLoadError={(error: any) => {
                                            console.log(error);
                                        }}
                                    >
                                        <Page
                                            scale={pdfScale * 0.01}
                                            pageNumber={pageNumber}
                                            onLoadSuccess={() =>
                                                getSchemaAnnotations(
                                                    sortedSchema()[
                                                        schemaIndex
                                                    ]?.[currentLocale] as string
                                                )
                                            }
                                            onLoadError={(error: any) => {
                                                console.log(error);
                                            }}
                                        />

                                        {coords.map((coord, index) => {
                                            return (
                                                <PartPreview
                                                    key={index}
                                                    coord={coord}
                                                    allParts={explodedParts}
                                                    currentLocale={
                                                        currentLocale
                                                    }
                                                />
                                            );
                                        })}
                                    </Document>
                                ) : (
                                    <>
                                        <div className='schema-zoom'>
                                            <button
                                                onClick={() => {
                                                    wzoom.zoomDown();
                                                }}
                                            >
                                                &#8722;
                                            </button>
                                            <button
                                                onClick={() => {
                                                    wzoom.zoomUp();
                                                }}
                                            >
                                                &#43;
                                            </button>
                                        </div>
                                        <div className='img-container'>
                                            {product?.schema &&
                                                product?.fields?.schema && (
                                                    <img
                                                        alt=''
                                                        className='parts-img'
                                                        id='parts-img'
                                                        ref={
                                                            imgRef as unknown as LegacyRef<HTMLImageElement>
                                                        }
                                                        src={`/file/${product?.schema?.[schemaIndex]?.[currentLocale]}`}
                                                        draggable={false}
                                                    />
                                                )}
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                        {endsWithPdf && hasMultipleSchema && (
                            <FontAwesomeIcon
                                className='arrow right'
                                icon={faChevronRight}
                                onClick={() => updateSchemaIndex(1)}
                            />
                        )}
                    </div>

                    <p>{t('parts.reference-plan-message')}</p>
                    <table className='part-list'>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th className='partInfo'>{t('parts.part')}</th>
                                <th className='partAvail'>
                                    {t('parts.price')}
                                </th>
                                <th className='partAvail'>
                                    {t('parts.delay')}
                                </th>
                                <th className='partCart'>&nbsp;</th>
                            </tr>
                        </thead>
                        <tbody>
                            {explodedParts
                                ?.map((part: any) => {
                                    if (
                                        part &&
                                        part.attributes &&
                                        part.attributes.schema_number &&
                                        part.attributes.schema_number.values &&
                                        part.attributes.schema_number
                                            .values[0] &&
                                        part.attributes.schema_number.values[0][
                                            currentLocale
                                        ] === '-'
                                    ) {
                                        part.attributes.schema_number.values[0][
                                            currentLocale
                                        ] = '888';
                                    }

                                    return part;
                                })
                                ?.filter(
                                    (a: any) =>
                                        a?.attributes?.schema_number?.values[0][
                                            currentLocale
                                        ] !== '-'
                                )
                                .sort((a: any, b: any) =>
                                    comparePart(
                                        a?.attributes?.schema_number?.values[0][
                                            currentLocale
                                        ],
                                        b?.attributes?.schema_number?.values[0][
                                            currentLocale
                                        ]
                                    )
                                )
                                .map((part: any, index: any) => {
                                    return <Part key={index} part={part} />;
                                })}
                        </tbody>
                    </table>

                    <br />
                    <p>{t('parts.part-info1')}</p>
                    <p>{t('parts.part-info2')}</p>
                </div>
            </div>
        </Layout>
    );
}

function Part({ part }: { part: any }) {
    const [qtyValue, setQtyValue] = useState(1);
    const [price] = useProductPrice(part?.code);
    const { currentLocale } = usePageLocalization();
    const availability = useAvailabilityInfo(part)?.availabilityLabel;
    const { t } = useTranslation('layout');
    if (part?.attributes?.schema_number?.values[0][currentLocale] == '888') {
        part.attributes.schema_number.values[0][currentLocale] = '-';
    }
    return (
        <tr>
            <td className='part-number'>
                <span>
                    {part?.attributes?.schema_number?.values[0][
                        currentLocale
                    ] ?? 0}
                </span>
            </td>
            <td className='part'>
                <span className='code'>{part?.code}</span>
                <br />
                <strong>{part?.title && part?.title[currentLocale]}</strong>
                <br />
                <span>
                    {
                        part?.attributes?.nb_per_appliance?.values[0][
                            currentLocale
                        ]
                    }
                    {t('parts.nb-per-appliance')}
                </span>
            </td>
            <td className='part-price'>
                {price?.regularPrice?.value === 0
                    ? '--'
                    : LocalePrice(currentLocale, price?.regularPrice?.value)}
            </td>
            <td className='partAvail'>
                {part?.code === '99998' ? (
                    <>--</>
                ) : availability ? (
                    availability
                ) : (
                    <>--</>
                )}
            </td>

            <td className='addToCart'>
                <AddToCartButtonContainer
                    product={part}
                    qtyValue={qtyValue}
                    setQtyValue={setQtyValue}
                    small={true}
                />
            </td>
        </tr>
    );
}

function PartPreview(props: { coord: any; allParts: any; currentLocale: any }) {
    const [isVisible, setIsVisible] = useState(false);
    const { coord, allParts, currentLocale } = props;
    const svgCart =
        'M9.384,19.186a1.7,1.7,0,1,1-1.7-1.7,1.7,1.7,0,0,1,1.7,1.7Zm11.355,0a1.7,1.7,0,1,1-1.7-1.7A1.7,1.7,0,0,1,20.739,19.186Zm0-7.381V4.992H7.113A1.136,1.136,0,0,0,5.977,3.856H2.571V4.992H4.842L5.695,12.3a2.271,2.271,0,0,0,1.418,4.044H20.739V15.211H7.113a1.136,1.136,0,0,1-1.136-1.136s0-.008,0-.012L20.739,11.8Z';
    const code = coord?.note?.split('//')?.[1].replace(':', '');

    const part = allParts.find((part: any) => part?.code === code);
    const [price] = useProductPrice(code);
    const [addProductToCart] = useAddProductsToCartAction();
    const { addAlert } = useContext(feedbackContext);
    const { setQuickCartVisible } = useCartContext();

    return (
        <React.Fragment>
            <div
                className='coord-test'
                style={{
                    left: coord.x,
                    top: coord.y,
                }}
                onClick={() => setIsVisible(!isVisible)}
            >
                {isVisible && (
                    <div
                        className='preview'
                        onClick={(event) => event.stopPropagation()}
                    >
                        <FontAwesomeIcon
                            icon={faTimes}
                            className='close'
                            onClick={() => setIsVisible(false)}
                        />
                        <div className='part-number'>
                            <span>
                                {
                                    part?.attributes?.schema_number?.values[0][
                                        currentLocale
                                    ]
                                }
                            </span>
                        </div>
                        <div className='title'>
                            <span>{part?.title[currentLocale]}</span>
                        </div>
                        <span className='code'>{part?.code}</span>
                        <div className='price'>
                            <span>
                                {price?.regularPrice?.value === 0
                                    ? '--'
                                    : LocalePrice(
                                          currentLocale,
                                          price?.regularPrice?.value
                                      )}
                            </span>
                        </div>
                        <div className='addToCart'>
                            <div
                                className='buy-icon'
                                onClick={(event) => {
                                    event.stopPropagation();
                                    setQuickCartVisible(true);
                                    addProductToCart(code, 1)
                                        .then((res) => {
                                            // console.log(res);
                                            // GTM GA4
                                            pushAddToCartEvent({
                                                id: code,
                                                price: price?.finalPrice.value,
                                                currency:
                                                    price?.finalPrice.currency,
                                                qty: 1,
                                            });
                                        })
                                        .catch((error) => {
                                            console.log(error);
                                            addAlert({
                                                message: error,
                                                status: 'error',
                                            });
                                        });
                                }}
                            >
                                <span>
                                    <span>+</span>
                                    <Icon
                                        d={svgCart}
                                        size='20'
                                        viewbox='0 0 18.169 17.033'
                                        transform='translate(-2.571 -3.856)'
                                    />
                                </span>
                            </div>
                        </div>

                        <div className='arrow-down' />
                    </div>
                )}
            </div>
        </React.Fragment>
    );
}
